//@ts-ignore
// @ts-nocheck
import isEqual from "lodash.isequal";
import { getArrayDuplicates, isNestedArray } from "../../../utils/functions";

/**
 * @file Request Splitter
 * @author Segun Adebayo and Godwin Benson
 *
 * Splits a client's request to determine how best to assign
 * teachers to the request.
 *
 * @param {Object} studentArray - Object of the client's request
 *
 * @returns {Object} Returns an object of options for the client to choose from.
 *
 * The returned Object has 1 or more of these 3 options:
 * 'One teacher' to teach all children
 * 'Specialized Teacher' to get teachers for each related subjects
 * 'Separate teacher' to get a teacher per child
 *
 * @see "__test__/request-splitter" for the test cases
 *
 */

let subjectGroupings = [
  // ["Literacy & Numeracy", "Phonics", "Handwriting"],
  [
    "Primary Math",
    "Primary Science",
    "JSS Math",
    "JSS Science",
    "Technology",
    "Technical Drawing",
    "SSS Math",
    "Physics",
    "F/Math",
  ],
  [
    "Primary English",
    "Creative Writing",
    "Phonics",
    "Literacy & Numeracy",
    "Handwriting",
    "JSS English",
    "SSS English",
    "Literature",
    "General Knowledge",
    "Government",
    "History",
  ],
  ["School Entrance Math", "School Entrance English", "General Knowledge"],
  [
    "13+ History",
    "IGCSE History",
    "Edexcel History",
    "SAT U.S. History",
    "AP U.S. History",
    "SAT History",
    "IB History",
  ],
  [
    "13+ Spanish",
    "IGCSE Spanish",
    "Edexcel Spanish",
    "SAT Spanish",
    "AP Spanish",
  ],
  ["13+ French", "IGCSE French", "Edexcel French", "SAT French", "AP French"],
  ["13+ German", "IGCSE German", "Edexcel German", "SAT German", "AP German"],
  ["13+ Greek", "Edexcel Greek"],
  ["13+ Latin", "SAT Latin", "AP Latin"],
  ["SAT Chinese", "AP Chinese"],
  ["Edexcel Arabic"],
  [
    "11+ Math",
    "11+ Non-verbal",
    "13+ Math",
    "13+ Science",
    "Checkpoint Math",
    "Checkpoint Science",
    "Primary Checkpoint Math",
    "Primary Checkpoint Science",
    "IGCSE Math",
    "IGCSE Physics",
    "Edexcel Math",
    "SAT Physics",
    "Edexcel Physics",
    "SAT Math",
    "ACT Math",
    "ACT Science",
    "SAT Math I",
    "SAT Math II",
    "IB Math",
    "IB Physics",
    "AP Calculus",
    "AP Physics",
    "AP Statistics",
  ],
  [
    "11+ English",
    "11+ Verbal",
    "13+ English",
    "Checkpoint English",
    "Primary Checkpoint English",
    "IGCSE English",
    "IGCSE Literature",
    "Edexcel English",
    "SAT Literature",
    "Edexcel Literature",
    "SAT Reading",
    "SAT Writing",
    "ACT English",
    "ACT Reading",
    "ACT Writing",
    "IB English",
    "AP English",
    "AP Literature",
  ],
  [
    "IGCSE Business",
    "Edexcel Economics",
    "Edexcel Accounting",
    "Edexcel Business",
    "IB Economics",
  ],
  [
    "13+ Geography",
    "IGCSE Biology",
    "IGCSE Chemistry",
    "IGCSE Geography",
    "SAT Biology",
    "SAT Chemistry",
    "Edexcel Geography",
    "Edexcel Chemistry",
    "Edexcel Biology",
    "IB Biology",
    "IB Chemistry",
    "IB Geography",
    "AP Chemistry",
    "AP Geography",
  ],
  ["Chemistry", "Biology", "Agric. Science", "Geography"],
  ["Business Studies", "Economics", "Accounting", "Commerce"],
  ["AP Music"],
  ["ICT", "IGCSE Computer", "Edexcel ICT", "IB Computer", "AP Computer"],
  ["IELTS", "TOEFL", "PTE"],
];

/**
 * Gets the class category based on the class. "Preschool" is categorized as "Nursery"
 *
 * @param {String} studentClass - A string of the student's class
 *
 * @returns {String} Returns the category of the student
 * There can only be 3 categories - nursery, primary, secondary
 *
 * @example
 * getClassCategory("Primary 1")
 * //=> primary
 */
export function getClassCategory(studentClass) {
  if (studentClass === "Preschool") {
    return "Nursery";
  }
  return studentClass.split(" ")[0];
}

/**
 * Orders the class categories from "Nursery" to "SSS"
 *
 * @param {Array} studentClass - An array of students' classes
 *
 * @returns {Array} Returns the class categories ordered from nursery to secondary
 *
 * @example
 * orderClassCategories(["Primary","Nursery","JSS"])
 * //=> ["Nursery", "Primary", "JSS"]
 */
export function orderClassCategories(studentClasses) {
  let nurseryClasses = studentClasses.filter((item) => item === "Nursery");
  let primaryClasses = studentClasses.filter((item) => item === "Primary");
  let jssClasses = studentClasses.filter((item) => item === "JSS");
  let sssClasses = studentClasses.filter((item) => item === "SSS");

  return [...nurseryClasses, ...primaryClasses, ...jssClasses, ...sssClasses];
}

/**
 * From the class of all students, separate the secondary classes from the remaining
 * We need the secondary classes because they most likely need Specialized teachers
 *
 * @param {String[]} classes
 * @returns {Object} An object of secondaryClasses and remainingClasses
 *
 * @example
 * splitClasses(["Primary 1", "JSS 2", "SSS 3", "Nursery 1"])
 * //=> {secondaryClasses: ["JSS 2", "SSS 3"], remainingClasses: ["Primary 1", "Nursery 1"]}
 */

export function splitClasses(classes) {
  let secondaryClasses = classes.filter((item) => {
    return item.search("JSS") > -1 || item.search("SSS") > -1;
  });
  let remainingClasses = classes.filter(
    (item) => !secondaryClasses.includes(item),
  );
  return { secondaryClasses, remainingClasses };
}

const requestSplitter = (studentArray) => {
  // Merge the subjects off all children in the request. Returns {Array}
  function mergeSubjects(classes) {
    let subjects = [];
    if (classes) {
      studentArray
        .filter(({ classDetail }) => classes.includes(classDetail.class))
        .map(({ classDetail }) => subjects.push(...classDetail.subjects));
    } else {
      studentArray.map(({ classDetail }) =>
        subjects.push(...classDetail.subjects),
      );
    }

    return Array.from(new Set(subjects));
  }

  // Split the merged subjects into groups based on the `subjectGroupings` array
  // `subjectGroupings` is an array that combines related subjects
  function splitIntoGroups(classes) {
    let mergedSubjects = mergeSubjects(classes);
    let subjectGroup = subjectGroupings
      .map((group) =>
        mergedSubjects.filter((subject) => group.includes(subject)),
      )
      .filter((arr) => arr.length > 0);
    return subjectGroup;
  }

  /**
   * Get the class of all the students in the request
   * and add their respective categories
   */
  function getClassofStudents() {
    let classes = studentArray.map(({ classDetail }) => classDetail.class);
    let classCategories = classes.map((className) =>
      getClassCategory(className),
    );
    classCategories = orderClassCategories(classCategories);
    return { classes, classCategories };
  }

  /**
   * Gets the subjects of each child
   */
  function getSubjectsByChild() {
    return studentArray.map(({ classDetail }) => classDetail.subjects);
  }

  /**
   * Get the subjects of the children in the request based on the classes passed
   *
   * @param {String[]} classes - Array of classes
   *
   * @returns {Array} An array of the subjects
   */

  function getSubjects(classes) {
    let returned = classes.map((item) =>
      studentArray.find(({ classDetail }) => classDetail.class === item),
    );

    let result = [];
    returned.forEach(({ classDetail }) => {
      result = Array.from(new Set([...result, ...classDetail.subjects]));
    });

    return result;
  }

  /**
   * Splits the secondary subjects by related subjects, merges remaining subjects and combines results.
   * This applies mostly to classes involving nursery/primary and jss/sss categories
   *
   * @returns {Object} An object of the "Specialized teachers" teacher option.
   */

  function splitAndMerge() {
    let result = {};
    const { classes } = getClassofStudents();

    let { remainingClasses, secondaryClasses } = splitClasses(classes);
    let mergedRemaining = getSubjects(remainingClasses);

    if (mergedRemaining.length > 0) {
      result["Specialized teachers"] = [
        mergedRemaining,
        ...splitIntoGroups(secondaryClasses),
      ];

      return result;
    } else {
      /**
       *  If request only includes secondary categories we only split into subject groups
       */

      result["Specialized teachers"] = splitIntoGroups(secondaryClasses);
      return result;
    }
  }

  /**
   * Separates the subjects of secondary classes (JSS and SSS) from that of the remaining classes,
   * and splits each subject group.
   *
   */

  function separateAndMerge() {
    let result = {
      "One teacher": mergeSubjects(),
      "Separate teachers": getSubjectsByChild(),
      "Specialized teachers": splitIntoGroups(),
    };

    let { classes, classCategories } = getClassofStudents();
    let uniqueCategories = Array.from(new Set(classCategories));
    let isPrimaryAndJSSOnly = isEqual(uniqueCategories, ["Primary", "JSS"]);

    let { secondaryClasses, remainingClasses } = splitClasses(classes);
    let nurseryClasses = remainingClasses.filter(
      (item) => item.includes("Nursery") || item.includes("Preschool"),
    );
    let primaryClasses = remainingClasses.filter((item) =>
      item.includes("Primary"),
    );
    let sssClasses = secondaryClasses.filter((item) => item.includes("SSS"));

    if (secondaryClasses.length > 0 && nurseryClasses.length > 0) {
      delete result["One teacher"];
    }
    if (sssClasses.length > 0) {
      delete result["One teacher"];
      delete result["Separate teachers"];
    }

    if (secondaryClasses.length === 0) {
      let nurserySubjects = getSubjects(nurseryClasses);
      let primarySubjects = getSubjects(primaryClasses);

      result["Specialized teachers"] = [nurserySubjects, primarySubjects];
    } else if (isPrimaryAndJSSOnly) {
      result["Specialized teachers"] = splitIntoGroups();
    } else {
      result = {
        ...result,
        ...splitAndMerge(),
      };
    }

    return result;
  }

  /**
   * Generates the recommended teacher option for the class categories (Business Requirement)
   *
   * @param {Array} classes - Array of class categories of all the children
   * @returns {Array} - Array of the teacher options based on the class categories passed
   */

  function getTeacherOptions(classes) {
    let isOneTeacherOnly =
      isEqual(classes, ["Nursery"]) || isEqual(classes, ["Nursery", "Nursery"]);

    let isSpecializedTeachersOnly =
      isEqual(classes, ["SSS"]) ||
      isEqual(classes, ["Nursery", "SSS"]) ||
      isEqual(classes, ["SSS", "SSS"]) ||
      isEqual(classes, ["JSS", "SSS"]);

    let isOneAndSpecializedTeachers =
      isEqual(classes, ["Primary"]) || isEqual(classes, ["JSS"]);

    let isAllTeacherOptions =
      isEqual(classes, ["Primary", "Primary"]) ||
      isEqual(classes, ["Primary", "JSS"]) ||
      isEqual(classes, ["JSS", "JSS"]);

    let isSeparateAndSpecializedTeachers = isEqual(classes, ["Nursery", "JSS"]);

    if (isEqual(classes, ["Nursery", "Primary"])) {
      return ["One teacher", "Separate teachers"];
    }
    if (isAllTeacherOptions) {
      return ["One teacher", "Separate teachers", "Specialized teachers"];
    }
    if (isOneTeacherOnly) {
      return ["One teacher"];
    }
    if (isSpecializedTeachersOnly) {
      return ["Specialized teachers"];
    }
    if (isOneAndSpecializedTeachers) {
      return ["One teacher", "Specialized teachers"];
    }
    if (isSeparateAndSpecializedTeachers) {
      return ["Separate teachers", "Specialized teachers"];
    }
    return;
  }

  /**
   * Generates the recommended teacher options for the class categories as an object
   *
   * @returns {Object} - Object contains the available teacher options for the request along
   *  with the subject split for each teacher
   *
   * @example
   * generateResult()
   * //=> {"One teacher": [{}], "Separate teachers": [{},{}], "Specialized teachers": [{},{}]}
   */

  function generateResult() {
    const { classCategories } = getClassofStudents();
    let teacherOptions = getTeacherOptions(classCategories) || [];

    let result = {};
    teacherOptions.forEach((option) => {
      if (option === "One teacher") {
        result["One teacher"] = mergeSubjects();
      }
      if (option === "Specialized teachers") {
        result["Specialized teachers"] = splitIntoGroups();
      }
      if (option === "Separate teachers") {
        result["Separate teachers"] = getSubjectsByChild();
      }
    });
    return result;
  }

  /**
   * Defines the final output of the function. Ensures results accounts for > 2 children
   */
  function generateFinalOutput() {
    let { classes, classCategories } = getClassofStudents();

    if (isEqual(classCategories, ["Primary", "SSS"])) {
      return splitAndMerge();
    }

    if (classes.length > 2) {
      return separateAndMerge();
    }
    return generateResult();
  }

  return generateFinalOutput();
};

export default requestSplitter;

/**
 * Provides a more readaable output for the request
 * @param {Array} studentArray An Array of the students for the request
 * @param {String[]} subjectGroup The subject group we're currently using the find a Tutor
 * @param {String} teacherOption The selected teacher options. oneOf
 *
 * @return {{teacherOption: String, purposes: String[], class: String[], names: String[], specialNeeds:String[], subjectGroup: String[], forTeacher: String[]}}
 */

export function transformSubjectGroup(
  studentArray,
  subjectGroup,
  teacherOption,
) {
  let output = [];
  subjectGroup.map((subject) => {
    let result = [];
    studentArray.forEach((student) => {
      if (student.classDetail.subjects.includes(subject)) {
        result.push({
          name: student.firstName,
          specialNeeds: student.special_needs,
          purposes: student.classDetail.purpose,
          studentClass: student.classDetail.class,
          learningNeed: student.learningNeed,
          curriculum: student.curriculum,
        });
      }
    });
    result.map((item) => output.push(item));

    return output;
  });

  function removeDuplicates(key) {
    let result = output.map((item) => item[key]).filter(Boolean);
    return Array.from(new Set(result));
  }

  function getCurriculum(key) {
    let nestedCurriculums = removeDuplicates(key);
    return Array.from(new Set(nestedCurriculums.flat()));
  }

  function generateForTeacher(studentNames: any[]) {
    let foundStudents = studentNames.map((studentName) =>
      studentArray.find(({ firstName }) => firstName === studentName),
    );
    let studentSubjects = foundStudents.map(
      ({ classDetail }) => classDetail.subjects,
    );
    let subjectsToTeach = studentSubjects.map((subjects) =>
      subjects.filter((subject) => subjectGroup.includes(subject)),
    );

    let finalObj = foundStudents.map((student, index) => ({
      ...student,
      classDetail: {
        ...student.classDetail,
        subjects: subjectsToTeach[index],
      },
    }));

    return finalObj;
  }

  return {
    teacherOption,
    purposes: removeDuplicates("purposes"),
    class: removeDuplicates("studentClass"),
    names: removeDuplicates("name"),
    specialNeeds: removeDuplicates("specialNeeds"),
    learningNeed: removeDuplicates("learningNeed"),
    curriculum: getCurriculum("curriculum"),
    subjectGroup,
    forTeacher: generateForTeacher(removeDuplicates("name")),
  };
}

function removeDuplicateForTeacher(forTeacher, name) {
  return forTeacher.filter((item) => item.firstName === name);
}

function getSpecialNeeds(childArray, name) {
  let student = childArray.filter((child) => child.firstName === name);
  if (student.length > 0 && student[0].special_needs !== "None")
    return [student[0].special_needs];
  return [];
}

function getLearningNeed(childArray, name) {
  let student = childArray.filter((child) => child.firstName === name);
  if (student.length > 0) return [student[0].learningNeeds];
  return [];
}

function getCurriculum(childArray, name) {
  let student = childArray.filter((child) => child.firstName === name);
  if (student.length > 0) return [...student[0].curriculum];
  return [];
}

/**
 * Provides a more readable output for the request
 *
 * @param {Array} childArray An Array of the students for the request
 *
 * @returns {option: [{teacherOption: String, purposes: String[], class: String[], names: String[], specialNeeds:String[], subjectGroup: String[], learningNeed: String[], curriculum: String[],searchSubject: String, forTeacher: String[]}]}
 */
export function transformSplit(childArray) {
  let split = requestSplitter(childArray);

  let keys = Object.keys(split);
  let result = {};

  let values = Object.values(split).map((arr, index) =>
    !isNestedArray(arr)
      ? [transformSubjectGroup(childArray, arr, keys[index])]
      : arr.map((subjectGroup) =>
          transformSubjectGroup(childArray, subjectGroup, keys[index]),
        ),
  );

  keys.forEach((key, index) => (result[key] = values[index]));

  //Separate teacher option often have duplicates, this removes duplicate entries
  //In "name", "class", "purpose", "forTeacher", "specialNeeds"
  if (result["Separate teachers"]) {
    let initialValue = result["Separate teachers"];
    let students = [];
    initialValue.map((item) => students.push(...item.names));
    let duplicateNames = getArrayDuplicates(students);
    let nameIndex = 0;

    let finalValue = initialValue.map((child) => {
      if (
        duplicateNames.length > 0 &&
        duplicateNames.every((name) => child.names.includes(name))
      ) {
        let uniqueStudentObject = {
          ...child,
          names: [duplicateNames[nameIndex]],
          class: [child.class[nameIndex]],
          purposes: [child.purposes[nameIndex]],
          learningNeed: getLearningNeed(childArray, child.names[nameIndex]),
          curriculum: getCurriculum(childArray, child.names[nameIndex]),
          specialNeeds: getSpecialNeeds(childArray, child.names[nameIndex]),
          forTeacher: removeDuplicateForTeacher(
            child.forTeacher,
            duplicateNames[nameIndex],
          ),
        };
        nameIndex++;
        return uniqueStudentObject;
      } else return child;
    });

    result["Separate teachers"] = finalValue;
  }

  return result;
}
